import React, { useState, useEffect } from 'react';
import { AdminService } from '../../services/adminService';
import styles from '../../styles/admin/adminRightsManager.module.css';

interface AdminRightsManagerProps {
  userEmail: string;
  userName?: string;
  currentRole: string;
  currentRights: string[];
  onSave: () => void;
  onCancel: () => void;
}

const AVAILABLE_RIGHTS = ['All', 'Users', 'Feedback', 'Translations', 'SAA', 'AA', 'NA'];

const AdminRightsManager: React.FC<AdminRightsManagerProps> = ({
  userEmail,
  userName,
  currentRole,
  currentRights,
  onSave,
  onCancel
}) => {
  const [selectedRights, setSelectedRights] = useState<string[]>(currentRights || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSelectedRights(currentRights || []);
  }, [currentRights]);

  const toggleRight = (right: string) => {
    if (right === 'All') {
      // If selecting "All", clear all others
      if (selectedRights.includes('All')) {
        setSelectedRights([]);
      } else {
        setSelectedRights(['All']);
      }
    } else {
      // If selecting a specific right, remove "All" if it's selected
      const newRights = selectedRights.filter(r => r !== 'All');
      if (newRights.includes(right)) {
        setSelectedRights(newRights.filter(r => r !== right));
      } else {
        setSelectedRights([...newRights, right]);
      }
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      // Ensure user is an admin before granting rights
      if (currentRole !== 'admin') {
        setError('User must be an admin to have admin rights. Set role to admin first.');
        return;
      }

      await AdminService.updateUserAdminRights(userEmail, selectedRights);
      onSave();
    } catch (err: any) {
      console.error('Error updating admin rights:', err);
      setError(err.message || 'Failed to update admin rights');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.adminRightsManager}>
      <div className={styles.header}>
        <h3>Manage Admin Rights</h3>
        <p className={styles.userInfo}>
          {userName && <strong>{userName}</strong>}
          {userName && ' - '}
          <span>{userEmail}</span>
        </p>
      </div>

      {currentRole !== 'admin' && (
        <div className={styles.warning}>
          ⚠️ User must have "admin" role to grant admin rights. Please set the role to "admin" first.
        </div>
      )}

      <div className={styles.content}>
        <p className={styles.instructions}>
          Select which admin rights to grant this user:
        </p>

        <div className={styles.rightsGrid}>
          {AVAILABLE_RIGHTS.map(right => (
            <label key={right} className={styles.rightOption}>
              <input
                type="checkbox"
                checked={selectedRights.includes(right)}
                onChange={() => toggleRight(right)}
                disabled={loading || currentRole !== 'admin'}
              />
              <div className={styles.rightInfo}>
                <strong className={styles.rightName}>{right}</strong>
                <span className={styles.rightDescription}>
                  {getRightDescription(right)}
                </span>
              </div>
            </label>
          ))}
        </div>

        {selectedRights.includes('All') && (
          <div className={styles.info}>
            ℹ️ "All" rights grant full admin access to everything, including the ability to manage other admins.
          </div>
        )}

        {error && (
          <div className={styles.error}>
            {error}
          </div>
        )}
      </div>

      <div className={styles.actions}>
        <button
          onClick={onCancel}
          className={styles.cancelButton}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className={styles.saveButton}
          disabled={loading || currentRole !== 'admin'}
        >
          {loading ? 'Saving...' : 'Save Admin Rights'}
        </button>
      </div>
    </div>
  );
};

function getRightDescription(right: string): string {
  switch (right) {
    case 'All':
      return 'Full access to all admin features';
    case 'Users':
      return 'Manage users and admin rights';
    case 'Feedback':
      return 'Review and manage user feedback';
    case 'Translations':
      return 'Manage languages, assign language admins, and publish languages';
    case 'SAA':
      return 'Manage SAA program content';
    case 'AA':
      return 'Manage AA program content';
    case 'NA':
      return 'Manage NA program content';
    default:
      return 'Program-specific admin access';
  }
}

export default AdminRightsManager;



